import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useWorkspace } from 'hooks/useWorkspace';

import arrowLeftIcon from 'assets/arrow-left.svg';
import spinner from 'assets/remix-spinner.gif';
import removeFrameIcon from 'assets/remove_frame_icon.svg';

import UploadScreens from 'Pages/TeamDashboard/CreateReview/components/UploadScreens';
import { FLOW_CATEGORIES } from 'Pages/TeamDashboard/ProductLibrary/flowCategories';
import styles from 'Pages/TeamDashboard/ProductLibrary/styles.module.scss';

import { addFlowVersionAction, saveFlowAction } from 'Redux/Actions/productLibraryActions';

import FilesService from 'services/files';
import { classifyFlowImages } from 'services/productLibrary';

import { FLOW_SOURCE } from 'utlis/constants';
import { errorReactToastify } from 'utlis/toasts';

import CategoryDropdown from './CategoryDropdown';
import SubFlowDropdown from './SubFlowDropdown';

const CLASSIFY_PHASE = {
  IDLE: 'idle',
  CLASSIFYING: 'classifying',
  CLASSIFIED: 'classified',
  FAILED: 'failed',
};

const AddFlowView = ({ onBack, flowToAddVersion }) => {
  const dispatch = useDispatch();
  const { selectedWorkspace } = useWorkspace();
  const authUser = useSelector(state => state.userStore);

  const [uploadedImages, setUploadedImages] = useState([]);
  const [figmaUrl, setFigmaUrl] = useState('');
  const [classifyPhase, setClassifyPhase] = useState(CLASSIFY_PHASE.IDLE);
  const [classifyResult, setClassifyResult] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Manual categorization state
  const [manualCategory, setManualCategory] = useState('');
  const [manualSubFlow, setManualSubFlow] = useState('');

  // Holds latest screenshot File objects, in insertion order
  const imageFilesRef = useRef([]);
  // AbortController for any in-flight classify request
  const classifyAbortRef = useRef(null);

  const handleSetOriginalFiles = useCallback(files => {
    imageFilesRef.current = files;
  }, []);

  const handleCategoryChange = useCallback(categoryName => {
    const previousCategory = manualCategory || classifyResult?.category_name;

    setManualCategory(categoryName);

    // If category actually changed, auto-select first sub-flow from new category
    if (previousCategory !== categoryName) {
      const category = FLOW_CATEGORIES.find(cat => cat.name === categoryName);
      const firstSubFlow = category?.flows?.[0] || '';

      setManualSubFlow(firstSubFlow);
    }
  }, [classifyResult?.category_name, manualCategory]);

  const handleSubFlowChange = useCallback(subFlowName => {
    setManualSubFlow(subFlowName);
  }, []);

  // Auto-classify whenever uploadedImages changes; cancel any previous in-flight request
  // Skip classification when adding a version (we already know the flow name)
  useEffect(() => {
    if (classifyAbortRef.current) {
      classifyAbortRef.current.abort();
      classifyAbortRef.current = null;
    }

    if (uploadedImages.length === 0) {
      setClassifyPhase(CLASSIFY_PHASE.IDLE);
      setClassifyResult(null);

      return;
    }

    // Skip classification for versions
    if (flowToAddVersion) {
      return;
    }

    const controller = new AbortController();

    classifyAbortRef.current = controller;

    const doClassify = async () => {
      setClassifyPhase(CLASSIFY_PHASE.CLASSIFYING);

      try {
        // Screenshots already have File objects in imageFilesRef (set synchronously by UploadScreens).
        // Video / Figma frames only have a hosted URL — fetch those to build File objects.
        const files = await Promise.all(
          uploadedImages.map(async (img, index) => {
            if (controller.signal.aborted) {
              throw new DOMException('Aborted', 'AbortError');
            }

            const existingFile = imageFilesRef.current[index];

            if (existingFile) {
              return existingFile;
            }

            const response = await fetch(img.url, { signal: controller.signal });
            const blob = await response.blob();
            const ext = (blob.type.split('/')[1] || 'jpg').split('+')[0];

            return new File([blob], `frame_${index + 1}.${ext}`, { type: blob.type });
          })
        );

        if (controller.signal.aborted) {
          return;
        }

        // Retry up to 3 times before giving up
        const MAX_RETRIES = 3;
        let result;

        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          if (controller.signal.aborted) {
            return;
          }

          try {
            result = await classifyFlowImages(files, controller.signal);
            break;
          }
          catch (err) {
            if (err.name === 'AbortError') {
              throw err;
            }

            if (attempt < MAX_RETRIES - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            }
            else {
              throw err;
            }
          }
        }

        if (controller.signal.aborted) {
          return;
        }

        const categoryName = result.category || result.label?.category || '';
        const subFlowName = result.sub_flow || result.flow || result.label?.sub_flow || '';

        setClassifyResult({
          category_name: categoryName,
          sub_flow: subFlowName,
        });
        // Initialize manual state with AI results so user can edit
        setManualCategory(categoryName);
        setManualSubFlow(subFlowName);
        setClassifyPhase(CLASSIFY_PHASE.CLASSIFIED);
      }
      catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        setClassifyPhase(CLASSIFY_PHASE.FAILED);
      }
    };

    doClassify();

    return () => {
      controller.abort();
    };
  }, [flowToAddVersion, uploadedImages]);

  const handleRemoveImage = useCallback(index => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    imageFilesRef.current = imageFilesRef.current.filter((_, i) => i !== index);

    // Clear classification state when images are modified
    setClassifyResult(null);
    setClassifyPhase(CLASSIFY_PHASE.IDLE);
    setManualCategory('');
    setManualSubFlow('');
  }, []);

  const handleDragEnd = useCallback(result => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    // Reorder uploadedImages
    setUploadedImages(prev => {
      const items = [...prev];
      const [reorderedItem] = items.splice(sourceIndex, 1);

      items.splice(destinationIndex, 0, reorderedItem);

      return items;
    });

    // Reorder imageFilesRef (only if they exist)
    if (imageFilesRef.current && imageFilesRef.current.length > 0) {
      const files = [...imageFilesRef.current];
      const [reorderedFile] = files.splice(sourceIndex, 1);

      files.splice(destinationIndex, 0, reorderedFile);
      imageFilesRef.current = files;
    }

    // Clear classification state when images are reordered
    setClassifyResult(null);
    setClassifyPhase(CLASSIFY_PHASE.IDLE);
    setManualCategory('');
    setManualSubFlow('');
  }, []);

  const handleClearScreens = useCallback(() => {
    imageFilesRef.current = [];
    setUploadedImages([]);
    setFigmaUrl('');
    setClassifyResult(null);
    setClassifyPhase(CLASSIFY_PHASE.IDLE);
    setManualCategory('');
    setManualSubFlow('');
  }, []);

  // Memoize final categorization values
  const finalCategory = useMemo(
    () => manualCategory || classifyResult?.category_name,
    [manualCategory, classifyResult?.category_name]
  );

  const finalSubFlow = useMemo(
    () => manualSubFlow || classifyResult?.sub_flow,
    [manualSubFlow, classifyResult?.sub_flow]
  );

  const handleSave = useCallback(async () => {
    // Validation is already handled by isSaveDisabled
    if (isSaving || uploadedImages.length === 0) {
      return;
    }

    if (!flowToAddVersion && (!finalCategory || !finalSubFlow)) {
      return;
    }

    setIsSaving(true);

    try {
      const hasVideo = uploadedImages.some(img => img.isVideoFrame);
      const hasFigma = uploadedImages.some(img => img.isFigmaImage);
      const source = hasVideo
        ? FLOW_SOURCE.VIDEO
        : hasFigma
          ? FLOW_SOURCE.FIGMA
          : FLOW_SOURCE.SCREENSHOT;

      let imageUrls;

      if (source === FLOW_SOURCE.SCREENSHOT) {
        // Upload raw files to S3 with retry logic (up to 3 attempts per file)
        const uploadWithRetry = async (file, maxRetries = 3) => {
          let lastError;

          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
              const url = await FilesService.uploadFileFromLocal(file);

              return url;
            }
            catch (error) {
              lastError = error;

              if (attempt < maxRetries) {
                // Wait before retrying (exponential backoff: 1s, 2s, 3s)
                await new Promise(resolve => setTimeout(resolve, attempt * 1000));
              }
            }
          }

          // If all retries failed, throw the last error
          throw lastError;
        };

        try {
          imageUrls = await Promise.all(
            imageFilesRef.current.map(file => uploadWithRetry(file))
          );
        }
        catch (uploadError) {
          errorReactToastify('Failed to upload images to AWS. Please check your connection and try again.', {
            autoClose: 5000,
            position: 'bottom-center',
            hideProgressBar: true,
            style: { backgroundColor: '#FDF0F0', width: '470px' },
          });

          return; // Don't proceed with save if uploads failed
        }
      }
      else {
        // Video frames and Figma frames already have hosted URLs
        imageUrls = uploadedImages.map(img => img.url);
      }

      const assets = imageUrls.map((url, i) => ({ image_url: url, order: i + 1 }));

      // Save flow with retry logic (up to 3 attempts)
      const saveWithRetry = async (maxRetries = 3) => {
        let lastError;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            if (flowToAddVersion) {
              await dispatch(
                addFlowVersionAction(selectedWorkspace?.uuid, flowToAddVersion.uuid, {
                  source,
                  assets,
                })
              );
            }
            else {
              await dispatch(
                saveFlowAction(selectedWorkspace?.uuid, {
                  name: finalSubFlow,
                  source,
                  category_name: finalCategory,
                  assets,
                })
              );
            }

            return; // Success
          }
          catch (error) {
            lastError = error;

            if (attempt < maxRetries) {
              // Wait before retrying (exponential backoff: 1s, 2s, 3s)
              await new Promise(resolve => setTimeout(resolve, attempt * 1000));
            }
          }
        }

        // If all retries failed, throw the last error
        throw lastError;
      };

      await saveWithRetry();

      onBack();
    }
    catch (_err) {
      errorReactToastify(`Failed to ${flowToAddVersion ? 'add version' : 'save flow'}. Please try again.`, {
        autoClose: 5000,
        position: 'bottom-center',
        hideProgressBar: true,
        style: { backgroundColor: '#FDF0F0', width: '470px' },
      });
    }
    finally {
      setIsSaving(false);
    }
  }, [
    dispatch,
    finalCategory,
    finalSubFlow,
    flowToAddVersion,
    isSaving,
    onBack,
    selectedWorkspace?.uuid,
    uploadedImages,
  ]);

  const isAddingVersion = !!flowToAddVersion;
  const showUploadArea = uploadedImages.length === 0;
  const showResultArea = uploadedImages.length > 0;

  // Memoize save button disabled state
  const isSaveDisabled = useMemo(
    () => isSaving || uploadedImages.length === 0
      || classifyPhase === CLASSIFY_PHASE.CLASSIFYING
      || (!isAddingVersion && (!finalCategory || !finalSubFlow)),
    [isSaving, uploadedImages.length, isAddingVersion, finalCategory, finalSubFlow, classifyPhase]
  );

  return (
    <div className={styles.addFlowLayout}>
      <div className={styles.addFlowHeader}>
        <button className={styles.addFlowBackBtn} onClick={onBack}>
          <img src={arrowLeftIcon} alt="" width={16} height={16} />
          {isAddingVersion ? 'Add new version' : 'Add new flow'}
        </button>
      </div>

      {showUploadArea && (
        <div className={styles.addFlowUploadArea}>
          <UploadScreens
            uploadedImages={uploadedImages}
            setUploadedImages={setUploadedImages}
            setOriginalFiles={handleSetOriginalFiles}
            figmaUrl={figmaUrl}
            setFigmaUrl={setFigmaUrl}
            authUser={authUser}
            userProfile={authUser}
            hideHeader
            hideFooter
          />
        </div>
      )}

      {showResultArea && (
        <>
          <div className={styles.addFlowResultArea}>
            {!isAddingVersion && (
              <div className={styles.classifyStatus}>
                {classifyPhase === CLASSIFY_PHASE.CLASSIFYING && (
                  <div className={styles.classifyLoading}>
                    <img src={spinner} alt="Classifying" width={20} height={20} />
                    Classifying your flow...
                  </div>
                )}

                {(classifyPhase === CLASSIFY_PHASE.CLASSIFIED || classifyPhase === CLASSIFY_PHASE.FAILED) && (
                  <div className={styles.dropdownRow}>
                    <CategoryDropdown
                      value={finalCategory}
                      onChange={handleCategoryChange}
                    />
                    <SubFlowDropdown
                      categoryName={finalCategory}
                      value={finalSubFlow}
                      onChange={handleSubFlowChange}
                      disabled={!finalCategory}
                    />
                  </div>
                )}
              </div>
            )}

            {isAddingVersion && (
              <div className={styles.classifyStatus}>
                <div className={styles.classifyResult}>
                  <span className={styles.classifyCategory}>
                    {flowToAddVersion.name}
                  </span>
                  <span className={styles.classifyFlowName}>
                    Version
                    {' '}
                    {flowToAddVersion.current_version + 1}
                  </span>
                </div>
              </div>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="images" direction="horizontal">
                {provided => (
                  <div
                    className={styles.addFlowImageRow}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {uploadedImages.map((img, index) => {
                      const uid = img.uid || `img-${index}`;

                      return (
                        <Draggable key={uid} draggableId={String(uid)} index={index}>
                          {(given, snapshot) => (
                            <div
                              ref={given.innerRef}
                              {...given.draggableProps}
                              {...given.dragHandleProps}
                              className={`${styles.addFlowImageItem}
                              ${snapshot.isDragging ? styles.addFlowImageItemDragging : ''}`}
                            >
                              <div className={styles.addFlowImageWrapper}>
                                <img
                                  src={img.preview || img.url}
                                  alt={`Screen ${index + 1}`}
                                  className={styles.addFlowImageThumb}
                                />
                              </div>
                              <button
                                type="button"
                                className={styles.addFlowRemoveBtn}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleRemoveImage(index);
                                }}
                                aria-label={`Remove screen ${index + 1}`}
                              >
                                <img src={removeFrameIcon} alt="" width={30} height={30} />
                              </button>
                              <span className={styles.addFlowImageNumber}>
                                {index + 1}
                              </span>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>

          <div className={styles.addFlowFooter}>
            <button
              type="button"
              className={styles.clearScreensBtn}
              onClick={handleClearScreens}
            >
              Clear screens
            </button>
            <button
              type="button"
              className={styles.saveFlowBtn}
              onClick={handleSave}
              disabled={isSaveDisabled}
            >
              {isSaving ? 'Saving...' : (isAddingVersion ? 'Save version' : 'Save flow')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

AddFlowView.propTypes = {
  onBack: PropTypes.func.isRequired,
  flowToAddVersion: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    current_version: PropTypes.number.isRequired,
  }),
};

AddFlowView.defaultProps = {
  flowToAddVersion: null,
};

export default AddFlowView;
